import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, RefreshCw, ShoppingBag, Loader2, ChefHat, CheckCircle2, Ban, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = ['placed', 'accepted', 'preparing', 'ready', 'served'];
const STATUS_COLOR = {
  placed: '#D4AF37',
  accepted: '#00FFFF',
  preparing: '#FF8C00',
  ready: '#39FF14',
  served: '#9D00FF',
  cancelled: '#8B0000',
};
const STATUS_LABEL = {
  placed: 'Placed',
  accepted: 'Accepted',
  preparing: 'Preparing',
  ready: 'Ready',
  served: 'Served',
  cancelled: 'Cancelled',
};

const StatusPill = ({ status }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.3em] font-['JetBrains_Mono']"
    style={{ color: STATUS_COLOR[status] || '#fff', background: `${STATUS_COLOR[status]}12`, border: `1px solid ${STATUS_COLOR[status]}40` }}
  >
    {STATUS_LABEL[status] || status}
  </span>
);

const OrderRow = ({ order, onAdvance, onCancel, busy }) => {
  const nextIdx = STATUS_FLOW.indexOf(order.status);
  const nextStatus = nextIdx >= 0 && nextIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[nextIdx + 1] : null;
  const cancellable = order.status !== 'served' && order.status !== 'cancelled';

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-3xl border border-white/8 bg-white/[0.02]"
      data-testid={`order-row-${order.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-white font-['Outfit'] text-lg font-light">
              #{order.id.slice(0, 6).toUpperCase()}
            </p>
            <StatusPill status={order.status} />
            {order.table_no && (
              <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-['JetBrains_Mono']">
                Table {order.table_no}
              </span>
            )}
          </div>
          <p className="text-gray-500 text-xs mt-1 font-['IBM_Plex_Sans']">
            {order.guest_name || 'Guest'}{order.guest_phone ? ` · ${order.guest_phone}` : ''}
            {' · '}{new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <p className="text-white font-['JetBrains_Mono'] font-medium text-lg whitespace-nowrap">
          {formatCurrency(order.subtotal || 0)}
        </p>
      </div>

      <ul className="space-y-1.5 mb-4">
        {(order.items || []).map((it, i) => (
          <li key={i} className="flex items-baseline justify-between gap-3 text-sm text-gray-300 font-['IBM_Plex_Sans']">
            <span>
              <span className="text-gray-500 mr-2">{it.qty}×</span>
              {it.name}
              {it.variation_name && <span className="text-gray-500 ml-2 text-xs">({it.variation_name})</span>}
            </span>
            <span className="text-gray-400 font-['JetBrains_Mono'] text-xs">{formatCurrency((Number(it.price) || 0) * (it.qty || 1))}</span>
          </li>
        ))}
      </ul>

      {order.note && (
        <p className="text-yellow-500/80 text-xs italic mb-4 font-['IBM_Plex_Sans']">Note: {order.note}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {nextStatus && (
          <button
            onClick={() => onAdvance(order.id, nextStatus)}
            disabled={busy}
            className="px-4 h-9 rounded-full text-[11px] uppercase tracking-[0.25em] font-['JetBrains_Mono'] font-semibold bg-[#D4AF37] hover:bg-[#c19d2f] text-black transition-colors disabled:opacity-50 flex items-center gap-1.5"
            data-testid={`order-advance-${order.id}`}
          >
            {nextStatus === 'accepted' && <ClipboardList className="w-3.5 h-3.5" />}
            {nextStatus === 'preparing' && <ChefHat className="w-3.5 h-3.5" />}
            {nextStatus === 'ready' && <CheckCircle2 className="w-3.5 h-3.5" />}
            {nextStatus === 'served' && <CheckCircle2 className="w-3.5 h-3.5" />}
            Mark {STATUS_LABEL[nextStatus]}
          </button>
        )}
        {cancellable && (
          <button
            onClick={() => onCancel(order.id)}
            disabled={busy}
            className="px-4 h-9 rounded-full text-[11px] uppercase tracking-[0.25em] font-['JetBrains_Mono'] bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            data-testid={`order-cancel-${order.id}`}
          >
            <Ban className="w-3.5 h-3.5" />
            Cancel
          </button>
        )}
      </div>
    </motion.article>
  );
};

const ClientOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('active');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/petpooja/orders`, { params: { limit: 200 } });
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // auto-refresh every 8s
    return () => clearInterval(t);
  }, []);

  const advance = async (id, status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/petpooja/orders/${id}/status`, { status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this order?')) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/petpooja/orders/${id}/status`, { status: 'cancelled' });
      toast.success('Order cancelled');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy(false);
    }
  };

  const visible = orders.filter((o) => {
    if (filter === 'active') return o.status !== 'served' && o.status !== 'cancelled';
    if (filter === 'served') return o.status === 'served';
    if (filter === 'cancelled') return o.status === 'cancelled';
    return true;
  });

  return (
    <div className="min-h-screen bg-black text-white" data-testid="client-orders-page">
      <header className="sticky top-0 z-30 px-5 py-4 bg-black/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="text-gray-500 hover:text-white transition-colors flex items-center gap-2" data-testid="orders-back">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] uppercase tracking-[0.3em] font-['JetBrains_Mono']">Home</span>
          </button>
          <p className="text-[10px] text-gray-500 uppercase tracking-[0.4em] font-['JetBrains_Mono']">Orders</p>
          <button onClick={load} className="text-gray-400 hover:text-white transition-colors" data-testid="orders-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 py-8">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-['JetBrains_Mono']">
              ✦ Kitchen · Live
            </p>
            <h1 className="text-4xl font-['Outfit'] font-extralight tracking-tight mt-1">
              {visible.length} {filter === 'active' ? 'active' : filter} order{visible.length === 1 ? '' : 's'}
            </h1>
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-yellow-500/70 font-['JetBrains_Mono'] px-3 py-1.5 rounded-full bg-yellow-500/5 border border-yellow-500/20">
            Demo · Petpooja mocked
          </span>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar" data-testid="orders-filter">
          {[
            { key: 'active', label: 'Active' },
            { key: 'served', label: 'Served' },
            { key: 'cancelled', label: 'Cancelled' },
            { key: 'all', label: 'All' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`orders-filter-${f.key}`}
              className={`px-4 h-8 rounded-full text-[11px] uppercase tracking-[0.25em] font-['JetBrains_Mono'] font-semibold transition-colors flex-shrink-0 ${
                filter === f.key ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && !orders.length ? (
          <div className="text-center py-20">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mx-auto" />
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-10 h-10 text-gray-700 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-gray-500 text-sm font-['IBM_Plex_Sans']">
              No {filter === 'all' ? '' : filter} orders yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((o) => (
              <OrderRow key={o.id} order={o} onAdvance={advance} onCancel={cancel} busy={busy} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientOrdersPage;
